import {Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { concat, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { IMatrixDoctor } from 'src/app/_models/doctor.model';
import { DoctorsService } from 'src/app/_services/doctors.service';
import {PspCompanyEnum} from '../../enums/psp-company.enum';

@Component({
  selector: 'app-doctor-names-selector',
  templateUrl: './doctor-names-selector.component.html',
  styleUrls: ['./doctor-names-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DoctorNamesSelectorComponent)
    }
  ]
})
export class DoctorNamesSelectorComponent implements OnInit, OnChanges, ControlValueAccessor {
  matrixDoctorsList: IMatrixDoctor[];
  matrixDoctorsItems$: Observable<IMatrixDoctor[]>;
  matrixDoctorsTypeahead$ = new Subject<string>();
  filteredMatrixDoctorsList: IMatrixDoctor[] = [];
  doctorName: string;
  value: any;
  @Output() doctorNameChanged: EventEmitter<string> = new EventEmitter();
  @Input() companyId: number;
  @Input() specialtyId: number;
  disabled = true;

  constructor(private doctorsService: DoctorsService) {
    this.matrixDoctorsTypeahead$.subscribe(() => true);
  }

  ngOnInit(): void {
    this.disabled = this.companyId === null
        || ![PspCompanyEnum.Eva, PspCompanyEnum.Lilly].includes(this.companyId)
        || this.specialtyId === null;

    this.getAllMatrixDoctors();
  }

  ngOnChanges(): void {
    this.disabled = !this.companyId || !this.specialtyId;

    if (!this.companyId || !this.specialtyId) {
      return;
    }

    this.doctorName = null;
    this.getAllMatrixDoctors();
  }

  onChanged = () => {};

  onTouched = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  trackByFn(item: any): string {
    return item.doctorName;
  }

  onSortList(list: any, searchKey: string | number, searchTerm: string): any {
    // noinspection RegExpDuplicateCharacterInClass
    searchTerm = searchTerm
      .split(' ')
      .join('')
      .replace(/[^a-zA-Z0-9 ئلإيءألآلاؤآا-ى]/g, '')
      .toLowerCase();

    // noinspection RegExpDuplicateCharacterInClass
    return list
      .filter(
        (filteredItem: any) => filteredItem.doctorName?.split(' ')
          .join('')
          .replace(/[^a-zA-Z0-9 ئلإيءألآلاؤآا-ى]/g, '')
          .toLowerCase()
          .indexOf(searchTerm) > -1
      )
      .sort((a: any, b: any) => {
        const aStarts = a[searchKey].toLowerCase().startsWith(searchTerm);
        const bStarts = b[searchKey].toLowerCase().startsWith(searchTerm);

        if (aStarts && bStarts) {
          return a[searchKey].localeCompare(b[searchKey]);
        }

        if (aStarts && !bStarts) {
          return -1;
        }

        if (!aStarts && bStarts) {
          return 1;
        }

        return a[searchKey].localeCompare(b[searchKey]);
      });
  }

  onSearchMatrixDoctorsList(): void {
    this.matrixDoctorsItems$ = concat(
      of([...this.matrixDoctorsList]),
      this.matrixDoctorsTypeahead$.pipe(
        distinctUntilChanged(),
        switchMap((searchTerm: string) => {
          this.filteredMatrixDoctorsList = [...this.matrixDoctorsList];

          if (searchTerm) {
            this.filteredMatrixDoctorsList = this.onSortList(
              this.filteredMatrixDoctorsList,
              'doctorName',
              searchTerm
            );
          }

          return of(this.filteredMatrixDoctorsList);
        })
      )
    );
  }

  getAllMatrixDoctors(): void {
    if (this.disabled) {
      return;
    }

    this.doctorsService
      .getDoctorsByCompanyId(this.companyId, this.specialtyId)
      .subscribe((response) => {
        this.matrixDoctorsList = response.data.doctors.map((d: any, index: number) => ({
          id: index,
          doctorName: this.formatDoctorName(d)
        }));

        this.onSearchMatrixDoctorsList();
      });
  }

  modelChangeFn(event: any): void {
    this.onTouched();
    this.onChanged();
    this.doctorNameChanged.emit(event?.trim());
  }

  onClear(): void {
    this.doctorName = null;
  }

  private formatDoctorName({englishName, arabicName}): string {
    return [englishName, arabicName]
      .filter(name => name !== null && name !== undefined && name !== '')
      .join(' - ');
  }
}
